<template>
  <el-card v-loading="loading || parentLoading" shadow="never">
    <div v-if="leases.length > 0 && facility && (activeLease || customerFutureInvoices.length) && !parentLoading">
      <el-card body-style="padding: 0" shadow="never" class="card" :key="invoice._id" v-for="(invoice) of leases">

        <div class="lease-wrapper">
          <div class="lease-info-wrapper">
            <div class="missing-items warning-card" v-if="!activeLease.leaseStarted">
              Missing Items:
              <div v-if="invoice.pendingItems && invoice.pendingItems.length > 0">
                <el-tooltip v-for="item in invoice.pendingItems" :key="item._id"
                  :content="`${item.reason} ${item.value ? $options.filters.currencySign(item.value, $root.state.facilityData) : ''}`"
                  :style="{ marginRight: '0.5rem' }">
                  <el-tag>{{ item.key }}</el-tag>
                </el-tooltip>
              </div>
              <el-tag type="success" v-else>Ready</el-tag>
            </div>
            <div class="lease-info">
              <span>
                <span style="font-weight: bold">Lease Status:</span>
                {{ invoice.status }}
              </span>
              <span v-if="activeLease.leaseStarted">
                <span style="font-weight: bold">Finance Status:</span>
                {{ activeLease.status === 'RENTED' ? 'CURRENT' : activeLease.status }}
              </span>
              <span>
                <span style="font-weight: bold">Units Leased:</span>
                {{ invoice.unitsInfo ? invoice.unitsInfo.map(unit => unit.name).join(', ') : '' }}
              </span>
            </div>
            <div class="lease-info">
              <span v-if="invoice.start">
                <span style="font-weight: bold">Start Date:</span>
                {{ invoice.start | formatDate }}
              </span>
              <span v-if="invoice.end">
                <span style="font-weight: bold">End Date:</span>
                {{ invoice.end | formatDate }}
              </span>
              <span>
                <span style="font-weight: bold">Lease Balance:</span>
                {{ invoice.amount - invoice.amountPaid | currencySign($root.state.facilityData) }}
              </span>
              <span>
                <span style="font-weight: bold">Past Due:</span>
                {{ pastDue(invoice) | currencySign($root.state.facilityData) }}
              </span>
            </div>
            <el-tag v-if="invoice && invoice.leaseTransfer && (leaseTransfer && leaseTransfer.transfer && !leaseTransfer.transfer.completed)" is-dot class="item" type="warning">Lease Transfer In Process</el-tag>
          </div>
          <div class="lease-controls">
            <div class="buttons">
              <el-popconfirm title="This will delete the Lease and revert the overdue balance. Are you sure?"
                icon="el-icon-info" icon-color="red" confirm-button-type="danger" v-if="invoice && invoice.status === 'PENDING' && !invoice.leaseStarted
                  " @confirm="() => cancelLease(invoice)">
                <div>
                  This will delete the Lease and revert the overdue
                  balance.
                </div>
                <el-button slot="reference" type="danger" size="small" style="width: 100%">Cancel Lease</el-button>
              </el-popconfirm>
              <el-popover v-if="invoice.status !== 'PENDING' && invoice.end && !invoice.future" placement="top"
                trigger="hover" title="Confirm Move Out" effect="light">
                <div>
                  When the Complete Move-Out action is performed, it
                  will do two things: one is it will calculate any
                  additional billing owed.
                  <br />
                  This means that, for example, the customer tells us
                  the Move Out date is January 10.
                  <br />
                  But actually they move out on January 14, and the
                  manager does the Complete Move-Out and says the
                  move-out is on January 14.
                  <br />
                  This will then trigger the system to apply a charge to
                  the customer account as follows: Unit Billing Rate per
                  month / 30 days per month * amount of days stayed
                  extra + 50%.
                  <br />
                  In this example, say the billing rate is $1000 per
                  month and the customer stayed an extra 4 days. So we
                  would do $1000 / 30 * 4 days + 50% = $200.
                </div>
                <el-button slot="reference" type="danger" size="small" style="width: 100%"
                  @click="() => openCompleteLeaseDialog(invoice)">Complete Lease</el-button>
              </el-popover>
              <el-popover v-if="invoice && !invoice.leaseStarted && !invoice.future && !invoice.leaseTransfer
                " placement="top" trigger="hover" title="Start Lease" effect="light">
                <div>
                  This lease is still in pending state.
                  <br />
                  To start the lease the customer supposed to upload the
                  signed document and clear the dues.
                </div>
                <el-button slot="reference" type="success" size="small" style="width: 100%"
                  :loading="startLeaseButtonStatus" :disabled="!allowAccess('level_3')"
                  @click="() => startLease(invoice)">Start Lease</el-button>
              </el-popover>
              <el-popover v-else-if="invoice && !invoice.leaseStarted && !invoice.future && invoice.leaseTransfer"
              placement="top" trigger="hover" title="Start Lease" effect="light">
                <div>
                  This lease is still in pending state.
                  <br />
                  A past lease has an amount still due that must be paid before this lease can be started.
                  Please review past leases in the History tab to determine the amount due, collect a payment, and use the Transfer Funds button in the History tab to apply the funds to the past lease.
                </div>
                <el-button slot="reference" type="success" size="small" style="width: 100%"
                  :loading="startLeaseButtonStatus" :disabled="!allowAccess('level_3')"
                  @click="() => startLease(invoice)">Start Lease</el-button>
              </el-popover>
              <el-popover v-if="invoice.future && fundsNeeded(invoice) && fundsAvailable"
                placement="top" trigger="hover" effect="light">
                <div>
                  Transfer funds from wallet to this lease.
                </div>
                <el-button slot="reference" size="small" style="width: 100%"
                  @click="() => transferFunds(invoice)">Transfer Funds</el-button>
              </el-popover>
              <el-popover v-if="!invoice.future" placement="top" trigger="hover" effect="light"
                content="Send email lease history to customer">
                <el-button slot="reference" size="small" style="width: 100%" @click="sendInvoiceAsEmail">
                  Email Lease History
                </el-button>
              </el-popover>
              <el-button v-if="!invoice.future" size="small" style="width: 100%" @click="sendLastPeriodItemsInMail">
                Send Last Invoice
              </el-button>
              <el-button size="small" style="width: 100%" v-if="invoice.end && !invoice.future && !invoice.transfer" type="primary"
                :loading="loading" @click="() => dialogForUpdateEndDate = true">
                Update Moveout
              </el-button>
              <el-button size="small" style="width: 100%" v-if="invoice.end && !invoice.future" type="warning"
                :loading="loading" @click="() => resetEndDate(invoice)">
                Cancel Moveout
              </el-button>
              <el-button icon="el-icon-printer" size="small" style="width: 100%" type="secondary"
                @click="previewAccountStatement">
                Account Statement
              </el-button>
              <el-popover v-if="!invoice.future && !invoice.end" placement="top" trigger="hover" effect="light">
                <div>
                  Add end date for the lease, make sure the end date is
                  set according to the agreement. <br />
                  End date will be considered as move out date for the
                  Unit(s) included in this Lease!
                </div>
                <el-button slot="reference" style="width: 100%" type="primary" size="small"
                  @click="() => openEndDateDialog(invoice)">
                  Add End Date
                </el-button>
              </el-popover>
              <el-popover v-if="!invoice.future" placement="top" trigger="hover"
                content="Add additional charges to customer account with respect to this Lease." effect="light">
                <el-button slot="reference" type="primary" style="width: 100%" size="small"
                  @click="() => openLineItemDialog(invoice)">
                  Add Charges
                </el-button>
              </el-popover>
              <!-- <el-popover trigger="hover" content="Add insurance and add payment receipt" effect="light">
                <el-button v-if="invoice.status === 'PENDING'" type="primary" size="small">
                  Confirm Lease
                </el-button>
              </el-popover> -->
            </div>
            <el-collapse v-model="activeNames" @change="() => getSignersStatus(invoice)">
              <el-collapse-item title="Agreement Details" name="1">
                <el-card shadow="never" v-loading="signersLoading">
                  <template slot="header">
                    <el-button style="float: right; margin: 10px;" size="small" icon="el-icon-refresh"
                      @click="() => getSignersStatus(invoice)"></el-button>
                    <el-button style="float: right; margin: 10px;" v-if="!invoice.agreement" size="small" type="secondary"
                      :disabled="loading" @click="() => openLicenseAgreementDialog(invoice)">
                      Send License Agreement
                    </el-button>
                    <Uploader
                      v-if="allowAccess('level_1')"
                      style="float: right; margin: 10px;"
                      button-text="Custom Agreement"
                      @upload-file="response => addCustomerAgreement(invoice._id, response)"
                      no-tip
                      :upload-prefix="`customer/${id}/`"
                    />
                    <!-- <el-button size="small" type="warning" style="float: right; margin: 10px;" :disabled="!allowAccess('level_1')">
                      Custom Agreement
                    </el-button> -->
                    <el-button style="float: right; margin: 10px;"
                      v-if="invoice.agreement && agreementStatus !== 'Completed'" size="small" type="danger"
                      @click="() => cancelLicenseAgreement(invoice)">
                      Cancel License Agreement
                    </el-button>
                    <el-popover placement="top" trigger="hover" effect="light" content="Download signed agreement">
                      <a v-if="invoice.agreementSigned" :href="invoice.agreementOnS3" download slot="reference">
                        <el-button style="float: right; margin: 10px;" size="small" type="primary"
                          icon="el-icon-download"></el-button>
                      </a>
                    </el-popover>
                    <el-popover placement="top" trigger="hover" effect="light" content="Send reminder email to signers">
                      <el-button v-if="!invoice.agreementSigned && invoice.agreement" style="float: right; margin: 10px;"
                        size="small" type="primary" icon="el-icon-bell" slot="reference"
                        @click="() => sendReminderEmail(invoice)"></el-button>
                    </el-popover>
                  </template>
                  <el-table v-if="invoice.agreement && signers.length" :data="[...signers]">
                    <el-table-column label="Name" prop="name">
                    </el-table-column>
                    <el-table-column label="Email" prop="email">
                    </el-table-column>
                    <el-table-column label="Status" prop="computedStatus" class-name="status-style">
                    </el-table-column>
                    <el-table-column label="Type" prop="signActionType">
                    </el-table-column>
                  </el-table>
                </el-card>
              </el-collapse-item>
              <el-collapse-item title="Trigger Jobs" name="2" v-show="isDevelopment">
                <el-button v-if="!invoice.future" icon="el-icon-coordinate" size="small" type="secondary"
                  :loading="autopayLoading" @click="handleAutopay">
                  Trigger Auto Pay
                </el-button>
                <el-button v-if="!invoice.future" icon="el-icon-coordinate" size="small" type="warning"
                  :loading="latefeeLoading" @click="handleLatefee">
                  Trigger Late fee
                </el-button>
                <el-button v-if="!invoice.future" :disabled="!invoice.leaseStarted" icon="el-icon-coordinate" size="small"
                  type="danger" :loading="billingJobLoading" @click="handleBillingJob">
                  Trigger Billing Job
                </el-button>
              </el-collapse-item>
            </el-collapse>
          </div>
          <div class="line-items">
            <h4 style="text-align: left">Lease Line Items</h4>
            <CustomerLeaseLineItems
              :facilitySecurityFeeLabel="facilitySecurityFeeLabel"
              :handleCommandForLineItem="handleCommandForLineItem"
              :lineItemBackgroundColor="lineItemBackgroundColor"
              :getMonthValue="getMonthValue"
              :customer="customer"
              :invoice="invoice"
            />
          </div>
        </div>
      </el-card>
    </div>
    <div v-else-if="!facility">
      <el-card>
        <NoSiteMap />
      </el-card>
    </div>
    <div v-else-if="active && !activeLease">
      <el-empty description="No Leasing found for this facility"></el-empty>
    </div>
    <div v-else-if="!active && !customerFutureInvoices.length">
      <el-empty description="No Leasing found for this facility"></el-empty>
    </div>
    <el-dialog :visible.sync="licenseAgreementDialog" destroy-on-close width="75%">
      <CustomerRentalAgreement
        v-if="leaseSelectedForAgreement"
        :customer-id="id"
        :move-in="new Date(leaseSelectedForAgreement.start)"
        :move-out="selectedLeaseEndDate"
        :lease-price="leasePrice"
        :prorated-rent="computedProratedPrice"
        :deposit="totalDeposit"
        :facility="facility"
        :units="leaseSelectedForAgreement.units"
        @confirmed="agreementConfirmed"
        :lease-id="leaseSelectedForAgreement._id"
      />
    </el-dialog>
    <el-dialog id="update-end-date" :visible.sync="dialogForUpdateEndDate">
      <el-card v-loading="loading">
        <div slot="header">
          <el-alert title="info alert" type="info" :closable="false"
            description="Make sure that the end date is set according to the agreement." show-icon>
          </el-alert>
        </div>
        <el-date-picker v-model="updatedEndDate" type="date" :picker-options="datePickerOptions" placeholder="Move Out Date">
        </el-date-picker>
        <el-button :disabled="!updatedEndDate" @click="updateEndDate">
          Update
        </el-button>
      </el-card>
    </el-dialog>
    <el-dialog id="end-date" :visible.sync="dialogForEndDate">
      <el-card v-loading="loading">
        <div slot="header">
          <el-alert title="info alert" type="info" :closable="false"
            description="Make sure that the end date is set according to the agreement." show-icon>
          </el-alert>
        </div>
        <el-date-picker v-model="endDate" type="date" :picker-options="datePickerOptions" placeholder="Move Out Date">
        </el-date-picker>
        <el-button :disabled="!endDate" @click="addEndDateToInvoice">
          Add
        </el-button>
      </el-card>
    </el-dialog>
    <el-dialog v-if="activeLease" id="line-item" :visible.sync="dialogForLineItems" destroy-on-close>
      <el-card v-loading="loading">
        <div slot="header" class="clearfix">
          <h5>Add Additional Charges.</h5>
        </div>
        <el-form :model="newLineItem" :rules="rules" ref="lineItemForm">
          <el-form-item label="Description" :rules="[
            {
              required: true,
              message: 'Please add the description',
              trigger: 'blur',
            },
          ]"
          prop="description"
          >
            <el-input v-model="newLineItem.description"> </el-input>
          </el-form-item>
          <el-form-item label="Charge Type" prop="type" :rules="[
            {
              required: true,
              message: 'Please select charge type.',
              trigger: 'blur',
            },
          ]">
            <el-select size="small" v-model="newLineItem.chargeType">
              <el-option label="Deposit" value="DEPOSIT"> </el-option>
              <el-option label="Rent" value="RENT"> </el-option>
              <el-option label="Fee" value="FEES"> </el-option>
            </el-select>
          </el-form-item>
          <el-form-item v-if="newLineItem.chargeType === 'RENT'" label="Units" :rules="[
            {
              required: true,
              message: 'Please select unit.',
              trigger: 'blur',
            },
          ]"
          prop="unit"
          >
            <el-select v-model="newLineItem.unit">
              <el-option :key="unit.unit" v-for="unit of activeLease.unitsInfo" :value="unit.unit" :label="unit.name">
                {{ unit.name }}
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item v-if="newLineItem.chargeType === 'RENT'" label="Applied Month" prop="period">
            <el-date-picker v-model="newLineItem.period" type="month" placeholder="Pick a month">
            </el-date-picker>
          </el-form-item>
          <!-- <el-form-item v-if="newLineItem.chargeType === 'RENT'" label="Lease Next Billing">
            <el-date-picker
              v-model="newLineItem.period"
              type="month"
              placeholder="Pick a month">
            </el-date-picker>
          </el-form-item> -->
          <el-form-item v-if="newLineItem.chargeType === 'FEES'" label="Fee Type" prop="chargeType">
            <el-select size="small" v-model="newLineItemFeeType">
              <el-option value="Material Sales"></el-option>
              <el-option value="Damage"></el-option>
              <el-option value="Penalties/Violations"></el-option>
              <el-option value="Construction/Improvements"></el-option>
              <el-option value="Forklift Training"></el-option>
              <el-option value="Hourly Labour"></el-option>
              <el-option value="Other/Miscellaneous"></el-option>
              <el-option value="Utility Income"></el-option>
              <el-option value="Security" :label="facilitySecurityFeeLabel"></el-option>
              <el-option value="CC Convenience"></el-option>
              <el-option value="Late Fee"></el-option>
              <el-option value="Auction Fee"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="Amount" prop="amount"
          >
            <el-input step="0.01" type="number" min="1" size="small" v-model="newLineItem.amount">
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-button @click="addLineItem"> Add </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </el-dialog>
    <el-dialog title="Start Lease Policy" :visible.sync="startDialogWarning">
      <el-card>
        <el-table v-show="conflicts.length" :data="conflicts">
          <el-table-column prop="unit.name" label="Unit"></el-table-column>
          <el-table-column prop="moveIn" label="Conflict Move In"
            :formatter="(row, column, cellValue) => $options.filters.formatDate(cellValue, this.$root.state.facilityData)"></el-table-column>
          <el-table-column prop="customer.company" label="Customer"></el-table-column>
        </el-table>
        <span slot="header">According to Readyspaces policy, a lease can be started only when:</span>
        <ul v-if="activeLease">
          <li v-if="activeLease.agreement && signers.length && signers[0].status !== 'completed'" class="text item">
            Customer must first sign the agreement.
          </li>
          <li v-if="activeLease.amount - activeLease.amountPaid > 0" class="text item">There should be no over due amount.
          </li>
          <li v-if="!customer.insurances.length" class="text item">Customer must upload the insurance</li>
          <li v-if="!leaseCanBeStartedOnStartDate" class="text item">Lease cannot be started until the start date</li>
          <li v-if="!customer.identification || (customer.identification && !customer.identification.url)"
            class="text item">Customer identification must be uploaded</li>
        </ul>
      </el-card>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="startDialogWarning = false">Okay</el-button>
      </span>
    </el-dialog>
    <el-dialog :visible.sync="completeLeaseDialog" destroy-on-close>
      <MoveOutUnit v-if="activeLease && !activeLease.leaseTransfer" :customer-id="id" :facility="facility" @completed="closeCompleteLeaseDialog" />
      <TransferStepComplete v-else :customer-id="id" :facility="facility" @completed="closeCompleteLeaseDialog" :lease-transfer="leaseTransfer"/>
    </el-dialog>
    <el-dialog :visible.sync="dialogForVoidLineItem">
      <el-card v-loading="voidCreditLineItemLoading">
        <el-form>
          <el-form-item label="Reason for void" :rules="[{ required: true, message: 'Please give a valid reason' }]">
            <el-input v-model="creditLineItem.voidReason"></el-input>
          </el-form-item>
          <el-form-item>
            <el-popconfirm confirm-button-text='Confirm' cancel-button-text='Cancel' icon="el-icon-info" icon-color="red"
              :title="`Are you sure to void $ ${creditLineItem.voidValue}?`" @confirm="submitVoidLineItem">
              <el-button :disabled="!creditLineItem.voidReason" slot="reference">Void</el-button>
            </el-popconfirm>
          </el-form-item>
        </el-form>
      </el-card>
    </el-dialog>
    <el-dialog :visible.sync="managerSignerDialog">
      <el-card v-if="signers.length && signers[1].status !== 'completed'">
        <CustomerRentalAnvil v-if="managerSignerUrl" :url="managerSignerUrl" @completed="saveAgreement" />
      </el-card>
      <el-card v-if="signers.length && signers[0].status !== 'completed'">
        Customer did not signed on the agreement.
      </el-card>
    </el-dialog>
    <el-dialog :visible.sync="previewAccountStatementDialog" width="55%" destroy-on-close>
      <AccountStatment :lease="activeLease" :customer="{ ...customer }" />
    </el-dialog>
    <!-- <el-dialog :visible.sync="signerDialog">
      <el-card shadow="never">
        <el-form>
          <el-form-item label="Name">
            <el-input v-model="signerForm.name"></el-input>
          </el-form-item>
          <el-form-item label="Email">
            <el-input v-model="signerForm.email"></el-input>
          </el-form-item>
        </el-form>
      </el-card>
    </el-dialog> -->
  </el-card>
</template>

<script>
import axios from 'axios';
import { addDays, addMonths, startOfMonth, isBefore } from 'date-fns';
import { nanoid } from 'nanoid';
import { FACILITY_ACCOUNT_API_BASE, FACILITY_TRANSACTIONS_API_BASE, LEASING_API, EMAIL_API_BASE, UPLOAD_API, isDevelopment, UNIT_RENTAL_API_BASE } from '../env';
import RsAnvilApi from '../api/rsAnvilApi';
import AccountStatment from './AccountStatement';
import CustomerRental from './CustomerRental';
import CustomerRentalAgreement from './CustomerRentalAgreement';
import CustomerLeaseLineItems from './CustomerLeaseLineItems';
import CustomerRentalAnvil from './CustomerRentalAnviI';
import Uploader from './Uploader';
import PrintDocument from './PrintDocument';
import { invoiceTemplate, currentInvoiceTemplate } from '../utils/emailTemplates';
import { getLeaseTaxLineItem } from '../utils/pricing';
import MoveOutUnit from './MoveOutUnit';
import TransferStepComplete from './TransferStepComplete';
import getMonth, { setMoveinToUTC, setMoveoutToUTC } from '../utils/timing';
import { allowAccess } from '../utils/roles';
import { getFacility } from '../utils/commonApi';

export default {
  name: 'CustomerLease',
  props: {
    id: { type: String, required: true },
    facility: { type: String, required: true },
    customer: { type: Object, default: () => { } },
    active: { type: Boolean, default: true },
    canCompleteMoveout: { type: Boolean, default: false },
    parentLoading: { type: Boolean, default: false },
    leaseTransfer: { type: Object, default: () => {} },
  },
  components: {
    AccountStatment,
    CustomerRentalAnvil,
    CustomerRental,
    CustomerLeaseLineItems,
    PrintDocument,
    MoveOutUnit,
    CustomerRentalAgreement,
    Uploader,
    TransferStepComplete,
  },
  watch: {
    async facility() {
      await this.getCustomerInvoices();
      await this.getUnitRentals();
      await this.downloadRentalAgreements();
    },
    async id() {
      this.signers = [];
      await this.getCustomerInvoices();
      await this.getUnitRentals();
      await this.getSignersStatus();
      await this.downloadRentalAgreements();
    },
    leaseTransfer: {
      handler(newVal) {
        if (newVal && newVal.transfer) {
          // Refresh data when lease transfer changes
          this.getCustomerInvoices();
        }
      },
      deep: true,
    },
  },
  data() {
    const endLeaseDateValidator = (date) => {
      if (this.activeLease && new Date(this.activeLease.start) > new Date()) {
        const moveIn = new Date(
          new Date(this.activeLease.start).getTime(),
        );
        const moveOut = addDays(moveIn, 1);
        return date < moveOut;
      }
      const currentDate = new Date();
      const day = addDays(currentDate, -1);
      return date < day;
    };

    const amountValidator = (_rule, value, callback) => {
      const amount = Number(value);
      if (Number.isNaN(amount)) {
        return callback(new Error('Please input digits'));
      }

      if (amount <= 0) {
        return callback(new Error('Amount should be greater than 0'));
      }

      return callback();
    };
    return {
      rules: {
        amount: [
          { required: true, message: 'Please give a valid amount', trigger: 'change' },
          { validator: amountValidator, trigger: ['blur', 'change'] },
        ],
      },
      facilityData: {},
      customerInvoices: [],
      facilityAccount: {},
      facilityAccountExists: false,
      isDevelopment,
      voidCreditLineItemLoading: false,
      autopayLoading: false,
      latefeeLoading: false,
      billingJobLoading: false,
      unitRentals: [],
      // signerDialog: false,
      // signerForm: {
      //   name: '',
      //   email: '',
      // },
      activeNames: [],
      loading: true,
      conflicts: [],
      managerSignerDialog: false,
      dialogForEndDate: false,
      dialogForLineItems: false,
      startDialogWarning: false,
      completeLeaseDialog: false,
      dialogForLeaseTransfer: false,
      dialogForUpdateEndDate: false,
      managerSignatureUrl: null,
      managerSignerUrl: '',
      dialogForVoidLineItem: false,
      startLeaseButtonStatus: false,
      previewAccountStatementDialog: false,
      licenseAgreementDialog: false,
      leaseSelectedForAgreement: null,
      signersLoading: false,
      signers: [],
      creditLineItem: {
        voidReason: '',
        voidValue: 0,
        voidedBy: '',
        voidedAt: new Date(),
        id: '',
      },
      newLineItem: {
        description: '',
        chargeType: 'DEPOSIT',
        amount: 0,
        parsedAmount: 0,
        accountBalance: 0,
        period: new Date(),
        dueDate: new Date(),
        createdAt: new Date(),
        type: 'CREDIT',
        unit: null,
      },
      newLineItemFeeType: '',
      datePickerOptions: {
        disabledDate: endLeaseDateValidator,
      },
      endDate: null,
      updatedEndDate: null,
    };
  },
  async mounted() {
    await this.getData();
  },
  computed: {
    fundsAvailable() {
      if (!this.facilityAccount || !this.facilityAccount.wallet) { return false; }

      return this.facilityAccount.wallet > 0;
    },
    selectedLeaseEndDate() {
      if (!this.leaseSelectedForAgreement || !this.leaseSelectedForAgreement.end) { return null; }

      return new Date(this.leaseSelectedForAgreement.end);
    },
    facilitySecurityFeeLabel() {
      let label = 'Security';

      const facility = this.facilityData;
      if (facility && facility.securityFee && facility.securityFee.label !== '') {
        label = facility.securityFee.label;
      }

      return label;
    },
    agreementStatus() {
      if (this.signers.length) {
        switch (true) {
          case (this.signers[0].status !== 'completed'):
            return 'Waiting for customer';
          case (this.signers[0].status === 'completed' && this.signers[1].status !== 'completed'):
            return 'Waiting for manager';
          case (this.signers[0].status === 'completed' && this.signers[1].status === 'completed'):
            return 'Completed';
          default:
            return '';
        }
      } else {
        return '';
      }
    },
    leases() {
      if (this.active) {
        if (this.activeLease) {
          return [this.activeLease];
        }
        return [];
      }
      return this.customerFutureInvoices;
    },
    activeLease() {
      const [lease] = this.customerInvoices.filter(lease => lease && !lease.future);
      return lease;
    },
    customerFutureInvoices() {
      return this.customerInvoices.filter(lease => lease && lease.future && !lease.leaseTransfer);
    },
    computedProratedPrice() {
      let rentItems = 0;
      this.leaseSelectedForAgreement.lineItems.forEach((item) => {
        if (item.chargeType === 'RENT') {
          rentItems += item.amount;
        }
      });
      return rentItems;
    },
    leasePrice() {
      let price = 0;
      this.unitRentals.forEach((rental) => {
        if (rental.unitInvoice === this.leaseSelectedForAgreement._id) {
          price += rental.updatedPrice ? rental.updatedPrice : rental.pricing.price;
        }
      });

      return price;
    },
    totalDeposit() {
      let depositItems = 0;
      this.leaseSelectedForAgreement.lineItems.forEach((item) => {
        if (item.details && item.details === 'DEPOSIT') {
          depositItems += item.amount;
        }
      });
      return `${this.$options.filters.currencySign(Number(depositItems), this.$root.state.facilityData)}`;
    },
    leaseCanBeStartedOnStartDate() {
      const currentDate = new Date();
      const startDate = new Date(this.activeLease.start);
      if (currentDate === startDate || currentDate > startDate) {
        return true;
      }
      return false;
    },
  },
  methods: {
    async getData() {
      await this.getCustomerInvoices();
      await this.getFacilityData();
      await this.getUnitRentals();
      await this.downloadRentalAgreements();
      await this.getCustomerFacilityAccount();
    },
    async addCustomerAgreement(invoice, response) {
      this.loading = true;
      await axios.put(
        `${LEASING_API}/v1/${this.facility}/${invoice}/manual/agreement`,
        {
          agreementSigned: true,
          url: response.response.url,
          createdBy: this.$keycloak.tokenParsed.email,
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );
      await this.getCustomerInvoices();
      this.loading = false;
    },
    fundsNeeded(invoice) {
      return invoice.amount > invoice.amountPaid;
    },
    async getFacilityData() {
      this.loading = true;
      this.facilityData = await getFacility(this.facility, { token: this.$keycloak.token });
      this.loading = false;
    },
    async getCustomerFacilityAccount() {
      this.loading = true;
      if (!this.facility || !this.customer._id) {
        this.facilityAccountExists = false;
        this.facilityAccount = { wallet: 0, amount: 0, amountPaid: 0, deposit: 0, depositToBeRefunded: 0 };
        return;
      }
      try {
        const response = await axios.get(`${FACILITY_ACCOUNT_API_BASE}/v1/${this.facility}/${this.customer._id}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

        if (response.data) {
          this.facilityAccountExists = true;
          this.facilityAccount = response.data;
        }

        if (this.facilityAccount.amount || this.facilityAccount.amountPaid) {
          this.facilityAccount.wallet = this.facilityAccount.amountPaid - this.facilityAccount.amount;
        }
        this.loading = false;
      } catch (err) {
        this.loading = false;
        this.facilityAccountExists = false;
        this.facilityAccount = { wallet: 0, amount: 0, amountPaid: 0, deposit: 0, depositToBeRefunded: 0 };
      }
    },
    async transferFunds(invoice) {
      this.loading = true;
      try {
        await axios.patch(
          `${FACILITY_ACCOUNT_API_BASE}/v1/account/sync/customer/${this.customer._id}/facility/${this.facility}/lease/${invoice._id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );
      } catch (err) {
        this.$message.error({
          message: `There was an error transfering funds to the lease: ${err.message}`,
        });
      }
      await this.getData();
      this.loading = false;
    },
    async getCustomerInvoices() {
      this.loading = true;
      const response = await axios.get(
        `${LEASING_API}/v1/customer/${this.id}/leases`,
        {
          params: {
            facility: this.facility,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        },
      );
      this.customerInvoices = response.data;
      await this.getSignersStatus();
      this.loading = false;
    },
    // openSignerDialog(signer) {
    //   this.signerDialog = true;
    // },
    pastDue(lease) {
      if (!lease.amountToBePaid) {
        return 0;
      }

      return lease.amountToBePaid - lease.amountPaid > 0
        ? lease.amountToBePaid - lease.amountPaid : 0;
    },
    async downloadRentalAgreements() {
      const lease = this.activeLease;
      if (lease && lease.agreement && !lease.agreementOnS3) {
        if (this.active) {
          const signers = await this.getSignersStatus(lease);
          const agreementCompleted = signers.length >= 2
            && signers[0].status === 'completed'
            && signers[1].status === 'completed';
          if (agreementCompleted && !lease.agreementSigned && !lease.agreementOnS3) {
            await this.saveAgreement(lease);
          }
        }
      }
    },
    async getUnitRentals() {
      try {
        this.loading = true;
        const units = await axios.get(`${UNIT_RENTAL_API_BASE}/customer/${this.id}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
          params: {
            limit: 250,
            ...(this.facility ? { facility: this.facility } : {}),
            ...(this.search ? { status: this.search } : {}),
          },
        });
        this.loading = false;
        this.unitRentals = units.data.result;
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async sendReminderEmail(invoice) {
      const agreementString = invoice.agreement;
      const agreement = JSON.parse(agreementString);
      const rsAnvilClient = new RsAnvilApi(this.$keycloak.token);

      await rsAnvilClient.sendReminderToSigners(agreement.eid);
      this.$message.success({
        message: 'Sent email reminder to signer(s).',
      });
    },
    async cancelLicenseAgreement(lease) {
      const agreementString = lease.agreement;
      const agreement = JSON.parse(agreementString);
      const rsAnvilClient = new RsAnvilApi(this.$keycloak.token);

      await rsAnvilClient.cancelAgreement(agreement.eid, agreement.documentGroup.eid);
      await this.unlinkAgreementToLease(lease, agreement);
    },
    async openLicenseAgreementDialog(lease) {
      this.leaseSelectedForAgreement = lease;
      await this.getUnitRentals();
      this.licenseAgreementDialog = true;
    },
    allowAccess(role) {
      return allowAccess(role, this.$keycloak, this.$root.state.facility);
    },
    async handleBillingJob() {
      this.billingJobLoading = true;
      try {
        await axios.get(`${LEASING_API}/v1/${this.facility}/${this.activeLease._id}/trigger/billing`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.response && err.response.data ? err.response.data : err.message,
        });
      }

      await this.getCustomerInvoices();
      this.billingJobLoading = false;
    },
    async handleAutopay() {
      this.autopayLoading = true;

      try {
        await axios.post(`${FACILITY_TRANSACTIONS_API_BASE}/v1/${this.id}/intiate/autopay`, {}, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.response && err.response.data ? err.response.data : err.message,
        });
      }

      await this.getCustomerInvoices();

      this.autopayLoading = false;
    },
    async handleLatefee() {
      this.latefeeLoading = true;

      try {
        await axios.get(`${LEASING_API}/v1/${this.activeLease._id}/${this.facility}/intiate/latefee`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.response && err.response.data ? err.response.data : err.message,
        });
      }

      await this.getCustomerInvoices();
      this.latefeeLoading = false;
    },
    lineItemBackgroundColor(lineItem) {
      if (lineItem.voided) {
        return 'linear-gradient(35deg, #ff9966, #ff5e62)';
      } else if (lineItem.type === 'DEBIT' && lineItem.transactionType !== 'ADJUSTMENT') {
        return 'linear-gradient(35deg, #4B8673, #0f9b0f)';
      } else if (lineItem.details === 'TAX') {
        return 'linear-gradient(35deg, #AF0171, #790252, #E80F88)';
      } else if (lineItem.details === 'RENT') {
        return 'linear-gradient(35deg, #AF0171, #790252, #E80F88)';
      } else if (lineItem.details === 'FEES') {
        return 'linear-gradient(35deg, #AF0171, #790252, #E80F88)';
      } else if (lineItem.transactionType === 'ADJUSTMENT') {
        return 'linear-gradient(35deg, #F2CD5C, #A61F69, #F2921D)';
      }
      return 'linear-gradient(35deg, #009aca, #0c3da7)';
    },
    async agreementConfirmed(etchPacket) {
      this.loading = true;
      this.managerSignatureUrl = JSON.stringify(etchPacket);
      await this.linkAgreementToLease(this.managerSignatureUrl);
      await this.getSignersStatus(this.leaseSelectedForAgreement);
      this.licenseAgreementDialog = false;
      this.leaseSelectedForAgreement = null;
      this.activeNames = [];

      await this.getCustomerInvoices();
      this.loading = false;
    },
    async submitVoidLineItem() {
      this.voidCreditLineItemLoading = true;
      const voidItem = this.creditLineItem;
      try {
        await axios.patch(`${LEASING_API}/v1/${this.facility}/${this.activeLease._id}/void/credit/line/item`, {
          ...voidItem,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.dialogForVoidLineItem = false;
      } catch (err) {
        this.dialogForVoidLineItem = false;
      }

      await this.getCustomerInvoices();
      this.voidCreditLineItemLoading = false;
    },
    handleCommandForLineItem(command, invoiceLineItem) {
      if (command === 'void') {
        this.dialogForVoidLineItem = true;
        this.creditLineItem.id = invoiceLineItem._id;
        this.creditLineItem.voided = true;
        this.creditLineItem.voidValue = invoiceLineItem.amount;
        this.creditLineItem.voidedBy = this.$keycloak.tokenParsed.email;
      }
    },
    previewAccountStatement() {
      this.previewAccountStatementDialog = true;
    },
    getMonthValue(value) {
      return getMonth(value);
    },
    async closeCompleteLeaseDialog() {
      this.loading = true;
      this.$emit('reload');
      await this.getCustomerInvoices();
      this.completeLeaseDialog = false;
      this.loading = false;
    },
    async getConflicts(invoice) {
      this.loading = true;
      const response = await axios.get(`${LEASING_API}/v1/${this.facility}/conflicts`, {
        params: {
          lease: invoice._id,
          moveIn: invoice.start,
          ...(invoice.end ? { moveOut: invoice.end } : {}),
        },
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });

      this.conflicts = response.data;

      if (this.conflicts.length) {
        this.$alert(`
        <strong>There is an existing lease for one or multiple units on this lease that overlaps with start date of this lease.
          You must cancel the conflicting future lease(s) prior to starting this current lease.</strong>
        `, 'Conflict found!', {
          confirmButtonText: 'OK',
          dangerouslyUseHTMLString: true,
          // callback: (action) => {
          // this.$message({
          //   type: 'info',
          //   message: `action: ${action}`,
          // });
          // },
        });
      }
      this.loading = false;
      return response.data;
    },
    async startLease(invoice) {
      try {
        // Manager didn't sign and customer completed
        // if customer didn't complete, show that message
        this.startLeaseButtonStatus = true;
        const rentals = await this.getConflicts(invoice);
        if (rentals.length) {
          this.startDialogWarning = true;
          this.startLeaseButtonStatus = false;
          return;
        }
        const signers = await this.getSignersStatus(invoice);

        if (signers.length >= 2) {
          if (signers[0].status !== 'completed') {
            this.startDialogWarning = true;
            this.startLeaseButtonStatus = false;
            return;
          }
          if (signers[1].status !== 'completed') {
            await this.openManagerSignerDialog(invoice);
            this.startLeaseButtonStatus = false;
            return;
          }

          const agreementCompleted = signers[0].status === 'completed'
            && signers[1].status === 'completed';
          if (agreementCompleted && !invoice.agreementSigned && !invoice.agreementOnS3) {
            await this.saveAgreement(invoice);
          }
        }

        if (invoice.amountPaid < invoice.amount) {
          this.startDialogWarning = true;
          this.startLeaseButtonStatus = false;
          return;
        }

        this.loading = true;

        await axios.patch(
          `${LEASING_API}/v1/${this.facility}/${invoice._id}/start`,
          {},
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );
        this.$notify.success({
          title: 'Lease Started',
          message: 'Success!',
        });

        await this.getCustomerInvoices();
        this.$emit('reload');

        this.loading = false;
        this.startLeaseButtonStatus = false;
      } catch (err) {
        this.startLeaseButtonStatus = false;
        this.startDialogWarning = true;
        this.loading = false;
        this.$message.error({
          title: 'Error',
          message: err.response && err.response.data && err.response.data.message ?
            err.response.data.message : err.response.data,
        });
      }
    },
    async cancelLease(invoice) {
      try {
        this.loading = true;
        await axios.patch(
          `${LEASING_API}/v1/${this.facility}/${invoice._id}/cancel`,
          {},
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );
        this.$notify.success({
          title: 'Lease Cancelled',
          message: 'Success!',
        });

        await this.getCustomerInvoices();
        this.$emit('reload');
        this.loading = false;
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    openCompleteLeaseDialog() {
      console.log(this.canCompleteMoveout);
      if (!this.canCompleteMoveout) {
        this.$message.warning({
          message: 'Can not complete this lease before getting license signed for next lease of the transfer.',
        });
        return;
      }
      this.completeLeaseDialog = true;
    },
    async linkAgreementToLease(agreement) {
      try {
        this.loading = true;
        await axios.put(
          `${LEASING_API}/v1/${this.facility}/${this.leaseSelectedForAgreement._id}`,
          {
            agreement,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        this.$emit('reload');
        this.loading = false;
        this.$notify.success({
          title: 'Success',
          message: agreement ? 'Agreement linked successfully.' : 'Agreement cancelled succesfully',
        });
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async unlinkAgreementToLease(lease, agreement) {
      try {
        this.loading = true;
        lease.events.push({
          type: 'agreement_cancelled',
          agreement,
          agreementOnS3: lease.agreementOnS3,
        });
        await axios.put(
          `${LEASING_API}/v1/${this.facility}/${lease._id}`,
          {
            agreement: '',
            agreementOnS3: '',
            agreementSigned: false,
            events: lease.events,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        this.$emit('reload');

        await this.getCustomerInvoices();
        this.loading = false;
        this.$notify.success({
          title: 'Success',
          message: 'Agreement cancelled succesfully',
        });
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async addEndDateToInvoice() {
      try {
        this.loading = true;
        await axios.patch(
          `${LEASING_API}/v1/${this.facility}/${this.activeLease._id}/start/lease/termination`,
          {
            end: setMoveoutToUTC(this.endDate),
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        await this.getCustomerInvoices();
        this.loading = false;
        this.dialogForEndDate = false;
        this.$notify.success({
          title: 'Success',
          message: 'End date added successfully.',
        });
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async addLineItem() {
      this.loading = true;
      this.$refs.lineItemForm.validate(async (valid) => {
        try {
          if (valid) {
            this.newLineItem.parsedAmount = Number(this.newLineItem.amount);
            const newLineItem = this.newLineItem;
            newLineItem.details = this.newLineItem.chargeType;
            newLineItem.addedBy = this.$keycloak.tokenParsed.email;
            this.newLineItem.custom = true;
            if (this.newLineItem.chargeType === 'FEES' && !this.newLineItemFeeType) {
              this.loading = false;
              this.$message.error({
                // title: 'Error',
                message: 'Please select Fee type.',
              });
              return;
            }

            const currentDate = new Date();
            if (this.newLineItem.chargeType === 'FEES' && this.newLineItemFeeType) {
              this.newLineItem.feeType = this.newLineItemFeeType;

              const firstOfNextMonth = startOfMonth(addMonths(currentDate, 1));
              const endDate = new Date(this.activeLease.end);
              // eslint-disable-next-line no-nested-ternary
              const firstOfMoveoutMonth = this.activeLease.end && isBefore(
                startOfMonth(endDate), firstOfNextMonth)
                ? startOfMonth(endDate) : null;

              const periodForFeeItem = firstOfMoveoutMonth || firstOfNextMonth;

              this.newLineItem.dueDate = setMoveinToUTC(
                periodForFeeItem,
              );
              this.newLineItem.period = setMoveinToUTC(
                periodForFeeItem,
              );
            } else if (this.newLineItem.chargeType === 'DEPOSIT') {
              this.newLineItem.dueDate = setMoveinToUTC(
                currentDate,
              );
              this.newLineItem.period = setMoveinToUTC(
                currentDate,
              );
            } else {
              this.newLineItem.dueDate = setMoveinToUTC(
                this.newLineItem.period,
              );
              this.newLineItem.period = setMoveinToUTC(
                this.newLineItem.period,
              );
            }

            if (this.newLineItem.chargeType === 'RENT' && !this.newLineItem.unit) {
              this.loading = false;
              this.$message.error({
                // title: 'Error',
                message: 'Please select the unit to add its rent.',
              });
              return;
            }

            this.newLineItem.accountBalance =
              (this.activeLease.amount +
                this.newLineItem.parsedAmount) -
              this.activeLease.amountPaid || 0;

            let taxItem;
            if (this.customer && !this.customer.taxExempted) {
              taxItem = getLeaseTaxLineItem(this.newLineItem, this.$root.state.facilityData);

              if (taxItem.amount) {
                const randomKey = nanoid();
                this.newLineItem.uniqId = randomKey;
                taxItem.source = randomKey;
              }
            }

            await axios.put(
              `${LEASING_API}/v1/${this.facility}/${this.activeLease._id}/add/line/item`,
              {
                newLineItem: {
                  ...newLineItem,
                  amount: newLineItem.parsedAmount,
                },
                ...(taxItem && taxItem.amount ? { taxItem } : {}),
              },
              {
                headers: {
                  Authorization: `Bearer ${this.$keycloak.token}`,
                },
              },
            );

            await this.getData();
            this.loading = false;
            this.dialogForLineItems = false;
            this.$notify.success({
              title: 'Success',
              message: 'Line Item added successfully.',
            });
          } else {
            this.loading = false;
          }
        } catch (err) {
          this.loading = false;
          this.$notify.error({
            title: 'Error',
            message: err.response && err.response.data ? err.response.data : err.message,
          });
        }
      });
    },
    openLineItemDialog() {
      this.dialogForLineItems = true;
    },
    openEndDateDialog() {
      this.dialogForEndDate = true;
    },
    async updateEndDate() {
      this.loading = true;
      try {
        await axios.patch(
          `${LEASING_API}/v1/${this.facility}/${this.activeLease._id}/lease/termination/update`,
          {
            end: this.updatedEndDate,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        await this.getCustomerInvoices();
        this.loading = false;
        this.$notify.success({
          title: 'Success',
          message: 'End date was updated successfully.',
        });
        this.dialogForUpdateEndDate = false;

        this.updatedEndDate = null;
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async resetEndDate() {
      this.loading = true;
      try {
        await axios.patch(
          `${LEASING_API}/v1/${this.facility}/${this.activeLease._id}/end/lease/termination`,
          {
            end: null,
            moveOutCancel: true,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );

        await this.getCustomerInvoices();
        this.loading = false;
        this.$notify.success({
          title: 'Success',
          message: 'End date was removed successfully.',
        });
      } catch (err) {
        this.loading = false;
        this.$notify.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    getLeaseCustomerId(lease) {
      const customerId = lease && lease.customerId;
      if (!customerId) {
        return '';
      }

      // eslint-disable-next-line no-underscore-dangle
      return String(customerId.id || customerId._id || customerId);
    },
    async getSignersStatus(invoice) {
      const lease = !invoice ? this.activeLease : invoice;
      const rsAnvilClient = new RsAnvilApi(this.$keycloak.token);
      try {
        const document = lease && lease.agreement ? JSON.parse(lease.agreement) : null;
        if (document && document.eid) {
          this.signersLoading = true;
          const response = await rsAnvilClient.getSignersStatus(document.eid);
          const signers = response.data.data.etchPacket.documentGroup.signers;
          signers.forEach((item, index) => {
            // eslint-disable-next-line no-param-reassign
            item.computedStatus = index === 0 ? signers[0].status : this.agreementStatus;
          });

          const leaseCustomerId = this.getLeaseCustomerId(lease);
          if (!leaseCustomerId || leaseCustomerId === String(this.id)) {
            this.signers = signers;
          }
          this.signersLoading = false;
          return signers;
        }
      } catch (err) {
        console.log(err);
      }

      this.signersLoading = false;
      return [];
    },
    async openManagerSignerDialog(invoice) {
      const lease = invoice || this.activeLease;
      const etchPacket = JSON.parse(lease.agreement);
      const managerEid = etchPacket.documentGroup.signers[1].eid;
      const documentEid = etchPacket.documentGroup.eid;
      const rsAnvilApi = new RsAnvilApi(this.$keycloak.token);

      const response = await rsAnvilApi.getSignerEmbedUrl(managerEid, documentEid);

      if (response.errors) {
        this.$message.warning({
          title: 'Agreement signature needed',
          message: 'Please check your email and sign the document.',
        });
        this.managerSignerDialog = false;
        return;
      }
      this.managerSignerDialog = true;
      this.managerSignerUrl = response.url;
    },
    async saveAgreement(invoice) {
      const lease = invoice || this.activeLease;
      // eslint-disable-next-line no-underscore-dangle
      const leaseId = lease && (lease.id || lease._id);
      const currentFacility = this.facility;
      if (!lease || !leaseId || !lease.agreement) {
        return;
      }

      this.managerSignerDialog = false;
      this.loading = true;
      try {
        const facility = lease.facility && typeof lease.facility === 'object'
          // eslint-disable-next-line no-underscore-dangle
          ? lease.facility.id || lease.facility._id
          : lease.facility;
        const anvilClient = new RsAnvilApi(this.$keycloak.token);
        const etchPacket = JSON.parse(lease.agreement);
        const documentEid = etchPacket.documentGroup.eid;
        const agreementBuffer = await anvilClient.downloadAgreement(documentEid);

        const formData = new FormData();
        const buffer = new Uint8Array(agreementBuffer.data.data);
        const blob = new Blob([buffer], { type: 'application/pdf' });
        formData.append('document', blob, `${documentEid}.pdf`);

        const response = await axios.post(UPLOAD_API, formData, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        await axios.put(
          `${LEASING_API}/v1/${facility || currentFacility}/${leaseId}`,
          {
            agreementSigned: true,
            agreementOnS3: response.data.url,
          },
          {
            headers: {
              Authorization: `Bearer ${this.$keycloak.token}`,
            },
          },
        );
      } finally {
        this.loading = false;
      }
    },
    async sendEmail(url) {
      const attachment = [];
      attachment.push({
        name: 'Lease Agreement.pdf',
        type: 'application/pdf',
        url,
      });
      await axios.post(`${EMAIL_API_BASE}/send/email`, {
        from: this.$keycloak.tokenParsed.email,
        to: this.customer.email,
        customer: this.id,
        facility: this.facility,
        subject: 'Lease Agreement',
        body: 'Signed Lease Agreement',
        attachments: attachment,
      }, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
    },
    async sendLastPeriodItemsInMail() {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      const currentPeriodLineItems = this.activeLease.lineItems.filter((item) => {
        if (item.type === 'CREDIT') {
          const itemPeriod = new Date(item.period);
          if (itemPeriod.getMonth() === currentMonth && itemPeriod.getFullYear() === currentYear) {
            return item;
          }
        }
      });

      if (currentPeriodLineItems.length) {
        const emailTemplate = currentInvoiceTemplate(
          this.activeLease,
          [...currentPeriodLineItems],
          this.customer,
          this.$root.state.facilityData,
          getMonth(currentDate),
        );
        await axios.post(`${EMAIL_API_BASE}/send/email`, {
          from: this.$root.state.facilityData.email,
          to: this.customer.email,
          customer: this.id,
          facility: this.facility,
          subject: `Your ReadySpaces ${this.$root.state.facilityData.title} ${getMonth(currentDate)} Lease Invoice`,
          body: emailTemplate,
          mjml: true,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.$message.success({
          message: 'Email sent succesfully',
        });
      } else {
        this.$message.error({
          message: 'Current period invoice line items not found!',
        });
      }
    },
    async sendInvoiceAsEmail() {
      this.loading = true;
      try {
        const emailTemplate = invoiceTemplate(
          this.activeLease,
          [...this.activeLease.lineItems],
          this.customer,
          this.$root.state.facilityData,
        );
        await axios.post(`${EMAIL_API_BASE}/send/email`, {
          from: this.$root.state.facilityData.email,
          to: this.customer.email,
          customer: this.id,
          facility: this.facility,
          subject: `Your ReadySpaces ${this.$root.state.facilityData.title} Lease Account History`,
          body: emailTemplate,
          mjml: true,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.$message.success({
          message: 'Email sent succesfully',
        });
      } catch (err) {
        this.$message.error({
          message: err && err.message ? err.message : err.response.data,
        });
      }
      this.loading = false;
    },
  },
};
</script>
<style scoped>
.warning-card {
  border: solid 1px #e6a23c;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  background-color: #fdf6ec;
  border-color: #e6a23c;
  color: #e6a23c;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.missing-items {
  display: flex;
	align-items: center;
	gap: 1rem;
	text-align: left;
	margin-bottom: 1rem;
}

.lease-info-wrapper {
  display: flex;
  flex-direction: column;
  width: 100% !important;
  gap: 1rem;
}

.lease-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.lease-controls .el-button+.el-button {
  margin-left: 0;
}

.card {
  border: none;
}

.text.item {
  margin-bottom: 5px;
}

.status-style {
  text-transform: capitalize;
}

.lease-info {
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 1rem;
}

@media (min-width: 1024px) {
  .buttons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }
}

@media (min-width: 768px) {
  .lease-info-wrapper {
    grid-column: 1 / 4;
    grid-row: 1 / 2;
  }

  .lease-wrapper {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(5, 1fr);
    grid-template-rows: auto 1fr;
  }

  .line-items {
    grid-column: 1 / 4;
    grid-row: 2 / 3;
  }

  .lease-info {
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .lease-controls {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    grid-column: 4 / 6;
    grid-row: 1 / 3;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CustomerLease.vue